import { goodsList, materialsList, resourceList } from '@/common/constants';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';

const textForReward = {
  resource: [
    'Workers dragged in an old crate. Don’t ask from where.',
    'It was just standing there. Now it’s yours. Lucky you.',
    'The old storeroom opened by itself. Something useful inside.',
    'They say it dropped from the sky. Sounds like destiny.',
    'BAMS! Look here! Shiny, flashy — your gift is waiting.',
    'A truck passed by. Something fell off. You win.',
    'Someone found it, someone dropped it. You’re the one who gets it.',
    'No record in the logs, but the crate is here. That’s all that matters.',
    'An unlisted box showed up. Guess who it belongs to now?',
    'Smile — there’s something good inside. You can feel it.',
    'A random surprise… or a secret reward for the worthy?',
    'Someone clearly meant for you to find this. Almost poetic.',
    'Your workers adore you. So they overdelivered. Again.',
    'The universe is clearly on your side. Enjoy the box.',
    'All your hard work — finally noticed. Here’s something extra.',
    'You’ve been launching, building, closing contracts. Now, this is yours.',
    'Roll the dice. Open the box. Take your reward.',
    'You’ve been going all in. The game’s giving back.',
    'Every true builder deserves a surprise crate now and then.',
  ],
  material: [
    'Workers dragged in an old crate. Don’t ask from where.',
    'It was just standing there. Now it’s yours. Lucky you.',
    'The old storeroom opened by itself. Something useful inside.',
    'They say it dropped from the sky. Sounds like destiny.',
    'BAMS! Look here! Shiny, flashy — your gift is waiting.',
    'A truck passed by. Something fell off. You win.',
    'Someone found it, someone dropped it. You’re the one who gets it.',
    'No record in the logs, but the crate is here. That’s all that matters.',
    'An unlisted box showed up. Guess who it belongs to now?',
    'Smile — there’s something good inside. You can feel it.',
    'A random surprise… or a secret reward for the worthy?',
    'Someone clearly meant for you to find this. Almost poetic.',
    'Your workers adore you. So they overdelivered. Again.',
    'The universe is clearly on your side. Enjoy the box.',
    'All your hard work — finally noticed. Here’s something extra.',
    'You’ve been launching, building, closing contracts. Now, this is yours.',
    'Roll the dice. Open the box. Take your reward.',
    'You’ve been going all in. The game’s giving back.',
    'Every true builder deserves a surprise crate now and then.',
  ],
  product: [
    'Workers dragged in an old crate. Don’t ask from where.',
    'It was just standing there. Now it’s yours. Lucky you.',
    'The old storeroom opened by itself. Something useful inside.',
    'They say it dropped from the sky. Sounds like destiny.',
    'BAMS! Look here! Shiny, flashy — your gift is waiting.',
    'A truck passed by. Something fell off. You win.',
    'Someone found it, someone dropped it. You’re the one who gets it.',
    'No record in the logs, but the crate is here. That’s all that matters.',
    'An unlisted box showed up. Guess who it belongs to now?',
    'Smile — there’s something good inside. You can feel it.',
    'A random surprise… or a secret reward for the worthy?',
    'Someone clearly meant for you to find this. Almost poetic.',
    'Your workers adore you. So they overdelivered. Again.',
    'The universe is clearly on your side. Enjoy the box.',
    'All your hard work — finally noticed. Here’s something extra.',
    'You’ve been launching, building, closing contracts. Now, this is yours.',
    'Roll the dice. Open the box. Take your reward.',
    'You’ve been going all in. The game’s giving back.',
    'Every true builder deserves a surprise crate now and then.',
  ],
  cash_income: [
    'Your debtor had a sudden attack of conscience. Paid you back—with interest.',
    'You helped a sweet old lady cross the street. Turns out she owns an oil company.',
    'Extra effort, extra profit. That’s how it works.',
    'Tax office made a recalculation. For once, it worked out in your favor.',
    'Someone sent money to the wrong account. Too late to fix it now.',
    'Apparently you placed a bet… and won. Even if you don’t remember it.',
    'A surprise bonus. Why? Must be your charm.',
    'Someone left a suitcase labeled: ‘For those who try hard.’',
    'You smiled at the right moment—and the universe wired you some cash.',
    'Contracts aren’t the only source of money. Luck in RollCraft helps too.',
    'Suitcase? Always a good sign—especially when it rustles inside.',
    'Collect your greens. Spend freely. No guilt required.',
    'Accounting glitch? Gift from above? Doesn’t matter. It’s yours now.',
    'Those who build industrial empires attract income from every direction.',
    'Your accountant found clever ways to save on taxes. You’re welcome.',
    'Bar fight broke out. You tripped over a suitcase. Guess what’s inside?',
    'That school buddy cafe finally turned a profit. Took its time.',
    'Your bank is ridiculously generous. Cashback incoming.',
    'Your mom knows you’re all grown up… and still sends you cash anyway.',
  ],
  cash_consumption: [
    'Tax authorities got you cornered. A little bribe saved the day.',
    'You rushed to the office and sped past the limit. Fine hit your wallet first.',
    'Quick coffee stop turned costly — forgot to pay for parking.',
    'Office furniture started screaming. You replaced it out of pity.',
    'Team meeting got fancy. You treated everyone. Your budget cried.',
    'Missed the deadline. Lost the client. And the money walked with them.',
    'You really thought the lawyer would work for free?',
    'New stamp, fresh business cards, updated logo. Classic budget leak.',
    'Marketing test backfired. Had to fire and rehire. Costly experiment.',
    'You stopped for a beer. Then the party spirit took over your credit card.',
  ],
  tgem: [
    'Feels like a holiday, doesn’t it? Enjoy your well-earned T-Gem.',
    'Karma picked a favorite today — and it’s you.',
    'Bingo! Someone just pulled a T-Gem!',
    'If luck had a favorite player — it’d be you.',
    'Sing, dance, rejoice — T-Gem doesn’t drop every day!',
    'You’re insanely lucky today. T-Gem was the missing piece.',
    'You’ve earned this. No questions. Just take it.',
    'Keep rolling those dice — and watch the T-Gems pour into your pocket.',
  ],
};

const getTextKindByReward = (
  reward: 'cash' | ResourceTypeText | PlantTypeText | GoodsType | 'tgem',
  isNegative?: boolean,
): keyof typeof textForReward => {
  if (reward === 'cash') {
    return isNegative ? 'cash_consumption' : 'cash_income';
  }

  if (resourceList.includes(reward as ResourceTypeText)) {
    return 'resource';
  }

  if (materialsList.includes(reward as PlantTypeText)) {
    return 'material';
  }

  if (goodsList.includes(reward as string)) {
    return 'product';
  }

  return 'tgem';
};

const getRandomTextByRewardKind = (
  boxReward: Partial<
    Record<
      ResourceTypeText | PlantTypeText | GoodsType | 'cash' | 'tgem',
      number
    >
  > | null,
): string | null => {
  if (!boxReward) {
    return null;
  }

  const rewardData = Object.entries(boxReward)[0];

  if (!rewardData) {
    return null;
  }

  const reward = rewardData[0] as
    | ResourceTypeText
    | PlantTypeText
    | GoodsType
    | 'cash'
    | 'tgem';
  const value = rewardData[1];
  const isNegative = value < 0;
  const kind = getTextKindByReward(reward, isNegative);
  const textArray = textForReward[kind];
  const randomIndex = Math.floor(Math.random() * textArray.length);

  return textArray[randomIndex];
};

export { getRandomTextByRewardKind, getTextKindByReward, textForReward };
